import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useLanguage } from '../context/LanguageContext';

const heroBackdrop = {
  background: `
    radial-gradient(circle at 14% 24%, rgba(var(--color-gradient-end-rgb), 0.28), transparent 34%),
    radial-gradient(circle at 82% 22%, rgba(var(--color-gradient-start-rgb), 0.14), transparent 28%),
    radial-gradient(circle at 58% 74%, rgba(var(--color-gradient-end-rgb), 0.18), transparent 30%),
    linear-gradient(180deg, rgba(var(--color-gradient-end-rgb), 0.16), rgba(249, 250, 251, 0.94))
  `,
};

const ctaBackdrop = {
  background: `
    radial-gradient(circle at 18% 22%, rgba(var(--color-gradient-end-rgb), 0.22), transparent 34%),
    radial-gradient(circle at 84% 36%, rgba(var(--color-gradient-start-rgb), 0.16), transparent 28%),
    linear-gradient(180deg, rgba(var(--color-gradient-end-rgb), 0.18), rgba(249, 250, 251, 0.96))
  `,
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headlineCycle, setHeadlineCycle] = useState(0);
  const whyRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const nodes = document.querySelectorAll('[data-reveal="landing"]');

    if (!nodes.length) {
      return undefined;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return undefined;
    }

    // Reveal lower sections as they enter the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -10% 0px' }
    );

    nodes.forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setHeadlineCycle((current) => current + 1);
    }, 4200);

    return () => window.clearInterval(intervalId);
  }, []);

  const settings = {
    dots: true,
    infinite: true,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3500,
    arrows: false,
    pauseOnHover: true,
    pauseOnDotsHover: true,
    adaptiveHeight: true,
    lazyLoad: 'ondemand',
    swipeToSlide: true,
    rtl: false,
    responsive: [
      { breakpoint: 640, settings: { autoplaySpeed: 4000 } },
      { breakpoint: 480, settings: { speed: 350 } },
    ],
  };

  const handleHomeClick = () => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLearnMoreClick = () => {
    setMobileMenuOpen(false);
    whyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goTo = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const renderAnimatedHeadline = (text, delayOffset = 0) => (
    <h1 aria-label={text} className="text-3xl font-bold tracking-tight text-text sm:text-4xl lg:text-5xl">
      {language === 'hi' ? (
        <span>{text}</span>
      ) : (
        <>
          <span className="sr-only">{text}</span>
          <span key={`${text}-${headlineCycle}`} aria-hidden="true">
            {Array.from(text).map((character, index) =>
              character === ' ' ? (
                <span key={`${headlineCycle}-${text}-space-${index}`} className="headline-space" />
              ) : (
                <span
                  key={`${headlineCycle}-${text}-${character}-${index}`}
                  className="headline-letter"
                  style={{ '--letter-delay': `${delayOffset + index * 34}ms` }}
                >
                  {character}
                </span>
              )
            )}
          </span>
        </>
      )}
    </h1>
  );

  const heroSlides = [
    { id: 'brand', kind: 'brand' },
    { id: 'task', src: '/images/task.png', alt: 'Task Management' },
    { id: 'teams', src: '/images/teams.png', alt: 'Empowered Teams' },
    { id: 'cloud', src: '/images/cloud.png', alt: 'Cloud Security Features' },
  ];

  return (
    <div className="home-page min-h-screen bg-background text-text">
      <header className="sticky top-0 z-30 border-b border-border bg-white/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={handleHomeClick}
            className="flex shrink-0 items-center gap-2.5"
            aria-label={t('landing.homeAria')}
          >
            <img
              src="/images/putpens-logo-new.png"
              alt="PutPens Logo"
              className="h-9 w-9 object-contain md:h-10 md:w-10"
            />
            <span className="text-xl font-bold tracking-tight text-text md:text-2xl">PutPens</span>
          </button>

          <nav className="hidden items-center gap-6 md:flex">
            <button
              type="button"
              onClick={handleHomeClick}
              className="border-b-2 border-primary pb-2 text-sm font-semibold text-primary transition"
            >
              {t('landing.home')}
            </button>
            <button
              type="button"
              onClick={handleLearnMoreClick}
              className="pb-2 text-sm font-semibold text-secondary-text transition hover:text-primary"
            >
              {t('landing.learnMore')}
            </button>
            <button
              type="button"
              onClick={() => goTo('/faq')}
              className="pb-2 text-sm font-semibold text-secondary-text transition hover:text-primary"
            >
              {t('landing.faq')}
            </button>
            <button
              type="button"
              onClick={() => goTo('/support')}
              className="pb-2 text-sm font-semibold text-secondary-text transition hover:text-primary"
            >
              {t('landing.support')}
            </button>
            <button
              type="button"
              onClick={() => goTo('/company-selection')}
              className="secondary-btn !rounded-2xl !px-5 !py-2.5 text-sm"
            >
              {t('landing.login')}
            </button>
          </nav>

          <div className="relative flex items-center gap-3 md:hidden">
            <button
              type="button"
              onClick={() => goTo('/company-selection')}
              className="secondary-btn px-3.5 py-2 text-sm"
            >
              {t('landing.login')}
            </button>
            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="secondary-btn px-3.5 py-2 text-sm"
              aria-haspopup="menu"
              aria-expanded={mobileMenuOpen}
            >
              {t('landing.menu')}
            </button>
            {mobileMenuOpen && (
              <div className="dialog-surface absolute right-0 top-full z-20 mt-3 w-60 overflow-hidden p-3">
                <div className="space-y-1">
                  <button type="button" onClick={handleHomeClick} className="shell-nav-link w-full justify-start">
                    {t('landing.home')}
                  </button>
                  <button type="button" onClick={handleLearnMoreClick} className="shell-nav-link w-full justify-start">
                    {t('landing.learnMore')}
                  </button>
                  <button type="button" onClick={() => goTo('/faq')} className="shell-nav-link w-full justify-start">
                    {t('landing.faq')}
                  </button>
                  <button type="button" onClick={() => goTo('/support')} className="shell-nav-link w-full justify-start">
                    {t('landing.support')}
                  </button>
                </div>
                <div className="mt-3 border-t border-border pt-3">
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-muted">
                    {t('common.language')}
                  </label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="input-field w-full px-3 py-2 text-sm"
                    aria-label={t('common.language')}
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="overflow-hidden">
        <section className="relative border-b border-border">
          <div className="absolute inset-0" style={heroBackdrop} />
          <div className="ambient-orb left-[4%] top-20 h-36 w-36 sm:h-52 sm:w-52 lg:h-64 lg:w-64" />
          <div className="ambient-orb-alt right-[6%] top-16 h-40 w-40 sm:h-56 sm:w-56 lg:h-72 lg:w-72" />
          <div className="relative mx-auto w-full max-w-7xl px-4 pb-12 pt-4 sm:px-6 md:pb-16 md:pt-6 lg:px-8 lg:pb-20 lg:pt-8">
            <div className="grid items-center gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)] lg:gap-3 xl:gap-5">
              <div className="subtle-panel order-2 mx-auto w-full max-w-[19rem] p-3 intro-rise intro-delay-150 sm:max-w-[23rem] sm:p-4 lg:order-1 lg:max-w-[29rem] lg:justify-self-end">
                <div className="float-gentle">
                  <Slider {...settings}>
                    {heroSlides.map((slide) => (
                      <div key={slide.id} className="relative w-full">
                        <div className="relative w-full" style={{ paddingTop: '177.77%' }}>
                          {slide.kind === 'brand' ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 rounded-lg px-6 text-center sm:gap-6">
                              <img
                                src="/images/putpens-logo-new.png"
                                alt="PutPens Logo"
                                loading="lazy"
                                className="h-auto w-full max-w-[10rem] object-contain sm:max-w-[12rem] lg:max-w-[13rem]"
                              />
                              <div className="text-3xl font-bold tracking-tight text-text sm:text-4xl">PutPens</div>
                            </div>
                          ) : (
                            <img
                              src={slide.src}
                              alt={slide.alt}
                              loading="lazy"
                              className="absolute inset-0 h-full w-full rounded-lg object-contain"
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>
              </div>

              <div className="order-1 max-w-2xl lg:order-2">
                <p className="intro-rise mb-4 text-xs font-semibold uppercase tracking-[0.32em] text-primary sm:text-sm">
                  {t('landing.heroKicker')}
                </p>
                <div className="space-y-1 text-balance">
                  {renderAnimatedHeadline(t('landing.heroLine1'), 150)}
                  {renderAnimatedHeadline(t('landing.heroLine2'), 520)}
                  {renderAnimatedHeadline(t('landing.heroLine3'), 890)}
                </div>
                <p className="intro-rise intro-delay-300 mt-5 max-w-xl text-base leading-8 text-secondary-text lg:text-lg">
                  {t('landing.heroSubtitle')}
                </p>
                <div className="intro-rise intro-delay-300 mt-8 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate('/enquiry?via=get-started')}
                    className="primary-btn !rounded-2xl !px-7 !py-3.5 text-sm sm:text-base"
                  >
                    {t('landing.getStarted')}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/company-selection')}
                    className="text-sm font-semibold text-accent-blue underline underline-offset-4 transition hover:opacity-80 sm:text-base"
                  >
                    {t('landing.alreadyStarted')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section ref={whyRef} className="bg-white">
          <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div data-reveal="landing" className="mx-auto max-w-5xl text-center reveal-on-scroll">
              <h2 className="text-3xl font-bold tracking-tight text-primary sm:text-4xl">
                {t('landing.whyTitle')}
              </h2>
              <p className="mx-auto mt-5 max-w-4xl text-base leading-8 text-secondary-text md:text-xl">
                {t('landing.whyBody')}
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-3">
              <div data-reveal="landing" className="card reveal-on-scroll p-6 transition duration-200 hover:-translate-y-0.5 hover:shadow-soft">
                <div className="text-2xl font-semibold tracking-tight text-text sm:text-[1.7rem]">
                  {t('landing.feature1Title')}
                </div>
                <p className="mt-3 text-base leading-7 text-secondary-text">
                  {t('landing.feature1Body')}
                </p>
              </div>
              <div data-reveal="landing" className="card reveal-on-scroll reveal-delay-100 p-6 transition duration-200 hover:-translate-y-0.5 hover:shadow-soft">
                <div className="text-2xl font-semibold tracking-tight text-text sm:text-[1.7rem]">
                  {t('landing.feature2Title')}
                </div>
                <p className="mt-3 text-base leading-7 text-secondary-text">
                  {t('landing.feature2Body')}
                </p>
              </div>
              <div data-reveal="landing" className="card reveal-on-scroll reveal-delay-200 p-6 transition duration-200 hover:-translate-y-0.5 hover:shadow-soft">
                <div className="text-2xl font-semibold tracking-tight text-text sm:text-[1.7rem]">
                  {t('landing.feature3Title')}
                </div>
                <p className="mt-3 text-base leading-7 text-secondary-text">
                  {t('landing.feature3Body')}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="relative border-y border-border">
          <div className="absolute inset-0" style={ctaBackdrop} />
          <div className="ambient-orb left-[10%] bottom-8 h-36 w-36 sm:h-48 sm:w-48 lg:h-60 lg:w-60" />
          <div className="ambient-orb-alt right-[12%] top-10 h-32 w-32 sm:h-44 sm:w-44 lg:h-56 lg:w-56" />
          <div className="relative mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <div data-reveal="landing" className="max-w-2xl reveal-on-scroll">
                <h2 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">
                  {t('landing.ctaTitle')}
                </h2>
                <p className="mt-5 text-base leading-8 text-secondary-text lg:text-lg">
                  {t('landing.ctaBody')}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    type="button"
                    onClick={() => navigate('/enquiry?via=free-trial')}
                    className="primary-btn !rounded-2xl !px-7 !py-3.5 text-sm sm:text-base"
                  >
                    {t('landing.startTrial')}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/faq')}
                    className="text-sm font-semibold text-accent-blue underline underline-offset-4 transition hover:opacity-80 sm:text-base"
                  >
                    {t('landing.exploreFaq')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white">
          <div data-reveal="landing" className="mx-auto w-full max-w-5xl px-4 py-16 text-center reveal-on-scroll sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">
              {t('landing.needHelp')}
            </h2>
            <p className="mt-5 text-base leading-8 text-secondary-text lg:text-lg">
              {t('landing.helpBody')}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/support')}
                className="primary-btn !rounded-2xl !px-7 !py-3.5 text-sm sm:text-base"
                aria-label={t('landing.goSupport')}
              >
                {t('landing.goSupport')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/enquiry?via=contact-sales')}
                className="secondary-btn !rounded-2xl !px-7 !py-3.5 text-sm sm:text-base"
              >
                {t('landing.contactSales')}
              </button>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border bg-white">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-3 px-4 py-8 text-sm text-secondary-text sm:px-6 md:flex-row lg:px-8">
          <div>{t('landing.footer')}</div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/privacy-policy')}
              className="font-medium text-accent-blue underline underline-offset-4 transition hover:opacity-80"
            >
              Privacy Policy
            </button>
            <span aria-hidden="true">|</span>
            <button
              type="button"
              onClick={() => navigate('/terms')}
              className="font-medium text-accent-blue underline underline-offset-4 transition hover:opacity-80"
            >
              Terms &amp; Conditions
            </button>
          </div>
        </div>
      </footer>

      <style>{`
        .slick-slider,
        .slick-list,
        .slick-track,
        .slick-slide { background: transparent !important; border: none !important; box-shadow: none !important; }
        .slick-slide img { background: transparent !important; border: none !important; box-shadow: none !important; }
        .slick-dots { bottom: 8px !important; }
        .slick-dots li button:before { color: #A855F7 !important; font-size: 8px !important; }
        @media (min-width: 640px) { .slick-dots li button:before { font-size: 10px !important; } }
        .slick-dots li.slick-active button:before { color: #9333EA !important; }
      `}</style>
    </div>
  );
};

export default LandingPage;
